export interface ForecastDirtyState {
  has_unsaved_changes: boolean
  /** True when no baseline exists yet and the caller should adopt `current_signature` as the baseline. */
  adopt_baseline: boolean
}

/**
 * Decide whether the forecast tree has unsaved edits, comparing its signature against the last
 * saved one.
 *
 * `last_saved_signature` is empty only before the first load/save of a version. An *empty tree*
 * still has a signature (`"[]"`), so deleting the last root node counts as an edit — saving an
 * empty tree deletes every node server-side, and that must not happen silently.
 */
export function evaluate_forecast_dirty_state(
  current_signature: string,
  last_saved_signature: string
): ForecastDirtyState {
  if (!last_saved_signature) {
    return { has_unsaved_changes: false, adopt_baseline: true }
  }
  return {
    has_unsaved_changes: current_signature !== last_saved_signature,
    adopt_baseline: false,
  }
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("evaluate_forecast_dirty_state", () => {
    it("adopts the first signature as the baseline without marking dirty", () => {
      expect(evaluate_forecast_dirty_state('[{"id":"a"}]', "")).toEqual({
        has_unsaved_changes: false,
        adopt_baseline: true,
      })
    })

    it("is clean when the signature matches the baseline", () => {
      expect(evaluate_forecast_dirty_state('[{"id":"a"}]', '[{"id":"a"}]')).toEqual({
        has_unsaved_changes: false,
        adopt_baseline: false,
      })
    })

    it("is dirty when the signature differs from the baseline", () => {
      expect(evaluate_forecast_dirty_state('[{"id":"b"}]', '[{"id":"a"}]')).toEqual({
        has_unsaved_changes: true,
        adopt_baseline: false,
      })
    })

    it("is dirty after the last root node is deleted", () => {
      expect(evaluate_forecast_dirty_state("[]", '[{"id":"a"}]')).toEqual({
        has_unsaved_changes: true,
        adopt_baseline: false,
      })
    })

    it("is clean when an already-empty tree is untouched", () => {
      expect(evaluate_forecast_dirty_state("[]", "[]")).toEqual({
        has_unsaved_changes: false,
        adopt_baseline: false,
      })
    })
  })
}
